import { toaster } from "@/components/ui/toaster";
import { EstadosIntencionCompra } from "@/constants/enums/status/entity-status-ids";
import { AppRoutes } from "@/constants/routes";
import {
  getPurchaseOrderIntent,
  updatePurchaseOrderIntent,
} from "@/services/purchase-order-intent.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { FieldValues } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { PurchaseOrderIntentDetail } from "./purchase-order-intent-detail.schema";

interface BaseHandlerProps {
  id?: string;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

interface FetchDetailHandlerProps extends BaseHandlerProps {
  setLoadingDetail: (value: boolean) => void;
  setDetail: Dispatch<SetStateAction<PurchaseOrderIntentDetail | null>>;
  showLoad?: boolean;
}

export const fetchPurchaseOrderIntentDetailHandler = async ({
  id,
  navigate,
  fetchPermissions,
  setLoadingDetail,
  setDetail,
  showLoad = true,
}: FetchDetailHandlerProps) => {
  try {
    if (showLoad) setLoadingDetail(true);

    const response = await getPurchaseOrderIntent(Number(id));
    setDetail(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PURCHASE_ORDER_INTENT_DETAIL.replace(":id", String(id)),
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudo obtener el detalle de la intención de compra.",
        ),
      });
    }
  } finally {
    if (showLoad) setLoadingDetail(false);
  }
};

interface UpdateStatusHandlerProps extends BaseHandlerProps {
  estado: EstadosIntencionCompra;
  event?: FieldValues;
  setLoading: (value: boolean) => void;
  setOpenModal?: (value: boolean) => void;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
  successMessage: string;
  errorMessage: string;
}

export const updatePurchaseOrderIntentStatusHandler = async ({
  id,
  estado,
  event,
  navigate,
  fetchPermissions,
  setLoading,
  setOpenModal,
  fetchDetail,
  successMessage,
  errorMessage,
}: UpdateStatusHandlerProps) => {
  setLoading(true);
  try {
    await updatePurchaseOrderIntent({
      id: Number(id),
      estado,
      detalle: String(event?.request_detail ?? ""),
    });

    toaster.create({
      type: "success",
      title: successMessage,
    });

    await fetchDetail(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PURCHASE_ORDER_INTENT_DETAIL.replace(":id", String(id)),
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error as AxiosError, errorMessage),
      });
    }
  } finally {
    setLoading(false);
    setOpenModal?.(false);
  }
};
